import type {
  ICartDishItem,
  ICartMenuStorage,
  TCartSupplementsValue,
} from "@/types";
import {
  readCartMenuStorage,
  writeCartMenuStorage,
} from "@/components/cart/helpers/cartStorage";
import { emitCartChanged } from "@/components/cart/helpers/cartEvents";

export const sanitizeSupplementsValue = (
  supplements: TCartSupplementsValue
): TCartSupplementsValue => {
  return (supplements ?? [])
    .map((group) => ({
      supplementId: group.supplementId,
      options: (group.options ?? []).filter(
        (option) => (option.quantity ?? 0) > 0
      ),
    }))
    .filter((group) => group.options.length > 0);
};

const saveMenuStorage = (menuId: number, next: ICartMenuStorage) => {
  writeCartMenuStorage(menuId, next);
  emitCartChanged(menuId);
};

export const upsertCartDish = (menuId: number, draft: ICartDishItem) => {
  const current = readCartMenuStorage(menuId);
  const currentItemsByDishId = current.itemsByDishId ?? {};

  const dishKey = String(draft.dishId);

  const nextItem: ICartDishItem = {
    dishId: draft.dishId,
    portionId: draft.portionId,
    supplements: sanitizeSupplementsValue(draft.supplements),
    quantity: draft.quantity,
    sourceCategoryId: draft.sourceCategoryId,
  };

  const next: ICartMenuStorage = {
    itemsByDishId: {
      ...currentItemsByDishId,
      [dishKey]: nextItem,
    },
  };

  saveMenuStorage(menuId, next);
};

export const removeCartDish = (menuId: number, dishId: number) => {
  const current = readCartMenuStorage(menuId);
  const dishKey = String(dishId);

  if (!current.itemsByDishId?.[dishKey]) return;

  const { [dishKey]: _, ...rest } = current.itemsByDishId;

  const next: ICartMenuStorage = {
    itemsByDishId: rest,
  };

  saveMenuStorage(menuId, next);
};

export const getCartDish = (menuId: number, dishId: number) => {
  const storage = readCartMenuStorage(menuId);
  return storage.itemsByDishId?.[String(dishId)] ?? null;
};

export const setCartDishQuantity = (
  menuId: number,
  dishId: number,
  nextQuantity: number
) => {
  const current = readCartMenuStorage(menuId);
  const itemsByDishId = current.itemsByDishId ?? {};
  const dishKey = String(dishId);

  const existing = itemsByDishId[dishKey];
  if (!existing) return;

  const safeQuantity = Math.max(1, Math.trunc(Number(nextQuantity) || 1));

  if (safeQuantity === existing.quantity) return;

  const next: ICartMenuStorage = {
    itemsByDishId: {
      ...itemsByDishId,
      [dishKey]: {
        ...existing,
        quantity: safeQuantity,
      },
    },
  };

  saveMenuStorage(menuId, next);
};

export const changeCartDishQuantityBy = (
  menuId: number,
  dishId: number,
  delta: number
) => {
  const item = getCartDish(menuId, dishId);
  if (!item) return;

  setCartDishQuantity(menuId, dishId, item.quantity + delta);
};
